import { A11yModule, FocusMonitor } from '@angular/cdk/a11y';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  effect,
  ElementRef,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatFormField } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressBar } from '@angular/material/progress-bar';
import { MatTooltip } from '@angular/material/tooltip';
import { ConfirmDialog, FparComment } from '@core/models';
import { filter } from 'rxjs/operators';
import { ConfirmDialogComponent } from '../dialogs';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    A11yModule,
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatFormField,
    MatIconModule,
    MatInputModule,
    MatMenuModule,
    MatProgressBar,
    MatTooltip,
    ReactiveFormsModule,
  ],
  selector: 'app-fpar-comment',
  templateUrl: './fpar-comment.component.html',
  styleUrls: ['./fpar-comment.component.scss'],
  standalone: true,
})
export class FparCommentComponent {
  public textAreaCommentEl = viewChild<ElementRef>('textAreaComment');

  public allowReplies = input(false);
  public comment = input<FparComment>();
  public issueResolved = input(false);
  public readOnly = input(false);
  public saving = input(false);
  public tableFormIsDirty = input(false);

  public delete = output<number>();
  public edit = output<boolean>();
  public save = output<Partial<FparComment>>();

  public currentComment = signal<FparComment>(null);
  public isAddingReply = signal(false);
  public isEditing = signal(false);
  public isSaving = signal(false);

  private dialog = inject(MatDialog);
  private formBuilder = inject(UntypedFormBuilder);
  private focusMonitor = inject(FocusMonitor);

  public commentForm = this.formBuilder.group({
    id: [null],
    comment: [''],
  });

  public commentFormValueChangeSignal = toSignal(
    this.commentForm.valueChanges,
    { initialValue: null },
  );

  constructor() {
    effect((onCleanup) => {
      let timer: NodeJS.Timeout;
      // Resetting add form to ensure clean input state for persistent form.
      if (!this.saving()) {
        timer = setTimeout(() => {
          this.commentForm?.enable();
          this.commentForm?.reset();
          this.commentForm?.markAsPristine();
          this.isSaving.set(false);
          this.isAddingReply.set(false);
        }, 1000);
      }

      onCleanup(() => {
        clearTimeout(timer);
      });
    });

    effect(() => {
      this.commentFormValueChangeSignal();
      this.edit.emit(this.commentForm.dirty);
    });

    effect(() => {
      if (this.comment()?.id) {
        this.commentForm.get('comment').setValidators(Validators.required);
        this.commentForm.updateValueAndValidity();
      }
    });
  }

  public deleteComment(comment: FparComment): void {
    const data: ConfirmDialog = {
      confirmColor: 'warn',
      message: 'Are you sure you want to delete this comment?',
      title: 'Confirm Delete',
    };

    this.dialog
      .open(ConfirmDialogComponent, { data })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.currentComment.set(comment);
          this.isSaving.set(true);
          this.delete.emit(comment.id);
        }
      });
  }

  public disableEditComment(): void {
    if (!this.commentForm.dirty) {
      this.cancelEditComment();
      return;
    }

    const confirmDialogConfig: ConfirmDialog = {
      confirmColor: 'warn',
      message: 'You have unsaved changes.  Are you sure you want to cancel?',
      title: '',
    };

    this.dialog
      .open(ConfirmDialogComponent, {
        data: confirmDialogConfig,
      })
      .afterClosed()
      .pipe(filter((result) => result))
      .subscribe(() => this.cancelEditComment());
  }

  public enableAddReply(): void {
    this.cancelEditComment();
    this.isAddingReply.set(true);
    this.focusInput();
  }

  public enableEditComment(currentComment: FparComment): void {
    this.isAddingReply.set(false);
    this.currentComment.set(currentComment);
    this.commentForm.patchValue(currentComment);
    this.isEditing.set(true);
    this.focusInput();
  }

  public submitComment(): void {
    if (this.tableFormIsDirty()) {
      const data: ConfirmDialog = {
        confirmColor: 'warn',
        message:
          'There are unsaved changes to your table data that will be erased if you continue with saving this comment. \
Do you wish to proceed?',
        title: 'Unsaved Table Data',
      };

      this.dialog
        .open(ConfirmDialogComponent, { data })
        .afterClosed()
        .subscribe((result) => {
          if (result) {
            this.saveComment();
          }
          return;
        });
    } else {
      this.saveComment();
    }
  }

  private cancelEditComment(): void {
    this.isEditing.set(false);
    this.commentForm.reset();
    this.edit.emit(false);
  }

  private focusInput(): void {
    setTimeout(() => {
      this.focusMonitor.focusVia(this.textAreaCommentEl(), 'program');
    }, 0);
  }

  private saveComment(): void {
    this.isSaving.set(true);
    this.commentForm.disable();

    const payload = this.isAddingReply()
      ? {
          ...this.commentForm.value,
          parentCommentId: this.comment()?.id,
        }
      : this.commentForm.value;

    this.save.emit(payload);
  }
}
